import { defineStore } from 'pinia';
import axios from 'axios';
import { ref, computed } from 'vue';
import { useDay, useMonth, useDate } from '@utils';
import { useAuthStore } from '@stores';
import i18n from '../locales/i18n';

const { t } = i18n.global;

const apiUrl = import.meta.env.VITE_API_URL;

export const useSessionStore = defineStore('sessionStore', () => {
    const authStore = useAuthStore();

    const sessionsList = ref(localStorage.getItem('sessions') ? JSON.parse(localStorage.getItem('sessions')) : []);
    const selectedSession = ref(localStorage.getItem('selectedSession') || null);
    const progressList = ref(null);
    const blockedList = ref(null);
    const finishedList = ref(null);
    const sessionsForSelect = ref(null);
    const usersStat = ref(null);

    const counters = ref({
        totalCount: null,
        countToday: null,
        countTodayBlocked: null,
        countTodayFinished: null,
    });

    const topBlockedList = ref(null);
    const topCodeList = ref(null);

    const statsList = ref(null);

    const removeSessionFromList = (id) => {
        sessionsList.value = sessionsList.value.filter(({ session_id }) => session_id !== id);
    };

    const getSessions = async () => {
        try {
            const {
                data: { data: responseData },
            } = await axios.get(apiUrl + '/v1/sessions', {
                headers: {
                    Authorization: 'Bearer ' + authStore.userToken,
                },
            });
            if (responseData === 'SessionsNotFound') {
                sessionsList.value = [];
                sessionsForSelect.value = [];
                localStorage.removeItem('sessions');
                localStorage.removeItem('selectedSession');
                selectedSession.value = null;
                return;
            } else {
                if (JSON.stringify(sessionsList.value) !== JSON.stringify(responseData)) {
                    sessionsList.value = responseData;
                    localStorage.setItem('sessions', JSON.stringify(responseData));
                }
            }
            const isSessionValid = sessionsList.value.some(({ session_id }) => session_id === selectedSession.value);

            if (!isSessionValid) {
                selectedSession.value = sessionsList.value[0]?.session_id;
                localStorage.setItem('selectedSession', selectedSession.value);
            }

            sessionsForSelect.value = sessionsList.value.map((item) => {
                return {
                    id: item.session_id,
                    name: item.name,
                };
            });
        } catch (e) {
            console.log('error', e);
            // throw new Error(response.status);
        }
    };

    const getLastUser = async () => {
        try {
            const {
                data: { data: responseData },
            } = await axios.get(apiUrl + '/v1/examiners/filter?session_id=' + selectedSession.value, {
                headers: {
                    Authorization: 'Bearer ' + authStore.userToken,
                },
            });

            const { blocked, finished, in_progress } = responseData;
            progressList.value = in_progress;
            blockedList.value = blocked;
            finishedList.value = finished;
        } catch (e) {
            console.log('error', e);
        }
    };

    const getCounters = async () => {
        // TODO:  is not used
        try {
            const {
                data: { data: responseData },
            } = await axios.get(apiUrl + '/v1/report/daily?session_id=' + selectedSession.value, {
                headers: {
                    Authorization: 'Bearer ' + authStore.userToken,
                },
            });

            const { total, blocked, total_today, finished } = responseData;

            counters.value = {
                totalCount: total,
                countToday: total_today,
                countTodayBlocked: blocked,
                countTodayFinished: finished,
            };
        } catch (e) {
            console.log('error', e);
        }
    };

    const getTopBlocked = async () => {
        try {
            const {
                data: { data: responseData },
            } = await axios.get(apiUrl + '/v1/most_blocked/today?session_id=' + selectedSession.value, {
                headers: {
                    Authorization: 'Bearer ' + authStore.userToken,
                },
            });

            topBlockedList.value = responseData;
        } catch (e) {
            console.log('error', e);
        }
    };

    const getTopCode = async () => {
        try {
            const {
                data: { data: responseData },
            } = await axios.get(apiUrl + '/v1/statistics/cheating?session_id=' + selectedSession.value, {
                headers: {
                    Authorization: 'Bearer ' + authStore.userToken,
                },
            });

            topCodeList.value = responseData.map((item) => {
                return { name: computed(() => t(`violations.${item.code}.short`)), value: item.count };
            });
        } catch (e) {
            console.log('error', e);
        }
    };

    const getStats = async (type) => {
        try {
            const {
                data: { data: responseData },
            } = await axios.get(apiUrl + '/v1/report?session_id=' + selectedSession.value + '&type=' + type, {
                headers: {
                    Authorization: 'Bearer ' + authStore.userToken,
                },
            });

            const { blocked, finished, total } = responseData;

            const getItem = (array, item) => {
                return array.map((prop) => {
                    return prop[item];
                });
            };

            const getValue = (array, item) => {
                return array.map((prop) => {
                    switch (type) {
                        case 1:
                            return useDay(prop[item]);
                        case 2:
                            return useDate(prop[item]);
                        case 3:
                            return useMonth(prop[item]);
                        default:
                            return prop[item];
                    }
                });
            };

            const data = {
                xAxis: getValue(blocked, 'num'),
                total: getItem(total, 'count'),
                finished: getItem(finished, 'count'),
                blocked: getItem(blocked, 'count'),
            };

            statsList.value = data;
        } catch (e) {
            console.log('error', e);
        }
    };
    const getStatUsers = async (type) => {
        try {
            const {
                data: { data: responseData },
            } = await axios.get(apiUrl + '/v1/report/monthly?session_id=' + selectedSession.value, {
                headers: {
                    Authorization: 'Bearer ' + authStore.userToken,
                },
            });
            usersStat.value = {
                xAxis: responseData.map((item) => {
                    return useMonth(item.num);
                }),
                series: responseData.map((item) => {
                    return item.count;
                }),
            };
        } catch (e) {}
    };
    const changeSession = async (sessionId) => {
        selectedSession.value = sessionId;
        localStorage.setItem('selectedSession', sessionId);
        try {
            await getLastUser();
            await getCounters();
            await getTopBlocked();
            await getTopCode();
            await getStats(1);
            await getStatUsers();
        } catch (error) {
            console.log('error', error);
        }
    };

    const uploadInstructionFile = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await axios.post('https://proctoring-api.univora.uz/services/platon-core/web/v1/public/files/upload/category/instruction', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return data;
        } catch (e) {
            console.log(e);
        }
    };

    return {
        removeSessionFromList,
        getSessions,
        getLastUser,
        getCounters,
        changeSession,
        getTopBlocked,
        getTopCode,
        getStats,
        getStatUsers,
        sessionsForSelect,
        sessionsList,
        progressList,
        blockedList,
        finishedList,
        counters,
        selectedSession,
        topBlockedList,
        topCodeList,
        statsList,
        usersStat,
        uploadInstructionFile,
    };
});
